import React, { useEffect, useRef } from 'react';
import { Animated, Easing, Switch, View, StyleSheet, Platform, Pressable } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { NEUTRAL_ACCENT_IDS } from '@/constants/Colors';
import Icon from '@/components/Icon';
import PropTypes from 'prop-types';

// react-native-macos renders Switch as a native AppKit control that ignores
// trackColor/thumbColor and paints its own bezel, which looks like a gray box
// behind a mismatched thumb. Draw our own switch on macOS instead.
const MAC_TRACK_WIDTH = 38;
const MAC_TRACK_HEIGHT = 22;
const MAC_THUMB_SIZE = 18;
const MAC_THUMB_INSET = (MAC_TRACK_HEIGHT - MAC_THUMB_SIZE) / 2;

function MacSwitch({ value, onValueChange, trackColorTrue, trackColorFalse, disabled, accessibilityLabel }) {
  const progress = useRef(new Animated.Value(value ? 1 : 0)).current;

  useEffect(() => {
    Animated.timing(progress, {
      toValue: value ? 1 : 0,
      duration: 160,
      easing: Easing.out(Easing.quad),
      useNativeDriver: false,
    }).start();
  }, [progress, value]);

  const trackColor = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [trackColorFalse, trackColorTrue],
  });
  const thumbTranslate = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [MAC_THUMB_INSET, MAC_TRACK_WIDTH - MAC_THUMB_SIZE - MAC_THUMB_INSET],
  });

  return (
    <Pressable
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="switch"
      accessibilityState={{ checked: !!value, disabled: !!disabled }}
      onPress={() => onValueChange(!value)}
      disabled={disabled}
      hitSlop={6}
      style={disabled ? macStyles.disabled : null}
    >
      <Animated.View style={[macStyles.track, { backgroundColor: trackColor }]}>
        <Animated.View style={[macStyles.thumb, { transform: [{ translateX: thumbTranslate }] }]} />
      </Animated.View>
    </Pressable>
  );
}

MacSwitch.propTypes = {
  value: PropTypes.bool.isRequired,
  onValueChange: PropTypes.func.isRequired,
  trackColorTrue: PropTypes.string.isRequired,
  trackColorFalse: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
};

const macStyles = StyleSheet.create({
  track: {
    width: MAC_TRACK_WIDTH,
    height: MAC_TRACK_HEIGHT,
    borderRadius: MAC_TRACK_HEIGHT / 2,
    justifyContent: 'center',
  },
  thumb: {
    width: MAC_THUMB_SIZE,
    height: MAC_THUMB_SIZE,
    borderRadius: MAC_THUMB_SIZE / 2,
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 1.5,
  },
  disabled: {
    opacity: 0.5,
  },
});

function SwitchInput({ value, onValueChange, disabled = false, accessibilityLabel }) {
  const { colors, theme, accentBackgroundColor } = useTheme();

  const trackColorFalse = (() => {
    switch (theme) {
      case 'muted':
      case 'night':
      case 'smoke':
      case 'sepia':
        return colors.border_color_input;
      case 'black':
        return '#555';
      default:
        return colors.border_color_secondary;
    }
  })();

  const trackColorTrue = (() => {
    if (Platform.OS === 'windows') return colors.text_color_primary;
    if (accentBackgroundColor && !NEUTRAL_ACCENT_IDS.has(accentBackgroundColor)) {
      return colors.accent_color_primary;
    }
    return colors.color_success;
  })();

  const thumbColorFalse = (() => {
    if (Platform.OS === 'windows') return colors.text_color_primary;
    switch (theme) {
      case 'muted':
        return '#777';
      case 'night':
        return '#6B7299';
      case 'smoke':
        return '#6E7276';
      case 'black':
        return '#777';
      case 'sepia':
        return '#8E7F69';
      default:
        return colors.solid_color_primary;
    }
  })();

  // RNW honors thumbColor directly. A fixed white thumb disappears against
  // the neutral white on-track in dark appearance; invert it with the canvas.
  const thumbColorTrue = Platform.OS === 'windows' ? colors.solid_color_primary
    : Platform.OS === 'web' ? colors.accent_color_primary : '#FFFFFF';

  const styles = {
    trackColor: {
      false: trackColorFalse,
      true: trackColorTrue,
    },
    thumbColor: {
      false: thumbColorFalse,
      true: thumbColorTrue,
    },
  };

  if (Platform.OS === 'macos') {
    return (
      <MacSwitch
        value={!!value}
        onValueChange={onValueChange}
        trackColorTrue={trackColorTrue}
        trackColorFalse={trackColorFalse}
        accessibilityLabel={accessibilityLabel}
        disabled={disabled}
      />
    );
  }

  return (
    <View style={switchStyles.container}>
      <Switch
        value={value}
        onValueChange={onValueChange}
        accessibilityLabel={accessibilityLabel}
        disabled={disabled}
        trackColor={styles.trackColor}
        thumbColor={!!value ? styles.thumbColor.true : styles.thumbColor.false}
        ios_backgroundColor={trackColorFalse}
        accessibilityState={{ checked: !!value }}
      />
      {Platform.OS === 'web' && value && (
        <View style={switchStyles.iconContainer} pointerEvents="none">
          <Icon name="circle" size={8} color={styles.trackColor.true} solid />
        </View>
      )}
      {Platform.OS === 'web' && !value && (
        <View style={switchStyles.iconContainerOff} pointerEvents="none">
          <Icon name="xmark" size={10} color={styles.trackColor.false} solid />
        </View>
      )}
    </View>
  );
}

const switchStyles = StyleSheet.create({
  container: {
    position: 'relative',
  },
  iconContainer: {
    position: 'absolute',
    right:
      Platform.OS === 'web' ? 6 : 
      Platform.OS === 'ios' ? 11 :
      Platform.OS === 'android' ? 9 :
      6,
    top: '50%',
    transform: [{
      translateY: 
        Platform.OS === 'web' ? -4 :
        Platform.OS === 'ios' ? -5 :
        Platform.OS === 'android' ? -9 :
        -4
    }],
    opacity: Platform.OS === 'ios' ? 0.45 : 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconContainerOff: {
    position: 'absolute',
    left:
      Platform.OS === 'web' ? 5 : 
      Platform.OS === 'ios' ? 10 :
      Platform.OS === 'android' ? 8 :
      5,
    top: '50%',
    transform: [{
      translateY: 
        Platform.OS === 'web' ? -5 :
        Platform.OS === 'ios' ? -6 :
        Platform.OS === 'android' ? -10 :
        -5
    }],
    justifyContent: 'center',
    alignItems: 'center',
  },
});

SwitchInput.propTypes = {
  value: PropTypes.bool.isRequired,
  onValueChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default React.memo(SwitchInput);
